// Canvas & sails piece cards and their move forms (docs/canvas-plan.md), shared by the Customers boat
// section (canvasBoat.js), the Work Orders › Canvas board (canvasView.js) and the techs app.
// The owner draws the page and calls cardHTML()/addFormHTML() inside it, then wire(); the controller
// keeps the open form, what's typed in it, spot suggestions, the loft list, photos and history.
// English or Spanish (the techs app's language).

import { escapeHTML as esc, woLabel } from './utilities';
import { formatDateToYYYYMMDD } from './date-format';
import {
  KINDS, GROUPS, pieceName, kindLabel, statusLabel, actionLabel, groupLabel, actionsFor, isOverdue, describeLog, errorText,
  money, decidedText,
} from './canvasCore';
import {
  loadSpots, loadLofts, loadLog, addPiece, runAction, editPiece, removePiece, today, newPieceId, savePhoto as uploadPhoto, photoUrl,
  addRepair, removeRepair, recordRepairAnswer, addLoftInvoice,
} from './canvasData';

const WAITING_FIRST = { 'needs-approval': 0, sent: 1, approved: 2, declined: 3 };

const T = {
  add: { EN: '+ Add piece', ES: '+ Agregar pieza' },
  addTitle: { EN: 'Add a piece', ES: 'Agregar una pieza' },
  addPulledTitle: { EN: 'Add a piece you pulled', ES: 'Agregar una pieza que quitaste' },
  whatIsIt: { EN: 'What is it', ES: 'Qué es' },
  choose: { EN: 'Choose…', ES: 'Elegir…' },
  description: { EN: 'Description (optional)', ES: 'Descripción (opcional)' },
  descriptionPh: { EN: 'e.g. Aft, 3 panels; Tan Sunbrella', ES: 'p. ej. Popa, 3 paneles' },
  whereNow: { EN: 'Where is it now', ES: 'Dónde está ahora' },
  onBoatOpt: { EN: 'On the boat', ES: 'En el barco' },
  pulledOpt: { EN: 'Already pulled and stored', ES: 'Ya quitada y guardada' },
  storedAt: { EN: 'Stored at', ES: 'Guardada en' },
  storedAtIfPulled: { EN: 'Stored at (if pulled)', ES: 'Guardada en (si se quitó)' },
  newSpot: { EN: 'New spot', ES: 'Nuevo lugar' },
  spotPh: { EN: 'e.g. Canvas loft bin 12, In boat – V-berth', ES: 'p. ej. Altillo, caja 12' },
  wo: { EN: 'Work order (optional)', ES: 'Orden de trabajo (opcional)' },
  note: { EN: 'Note (optional)', ES: 'Nota (opcional)' },
  photo: { EN: 'Photo (optional)', ES: 'Foto (opcional)' },
  loft: { EN: 'Loft', ES: 'Velería' },
  loadingLofts: { EN: 'Loading vendors…', ES: 'Cargando…' },
  dueBack: { EN: 'Due back (optional)', ES: 'Regresa (opcional)' },
  save: { EN: 'Save', ES: 'Guardar' },
  saving: { EN: 'Saving…', ES: 'Guardando…' },
  cancel: { EN: 'Cancel', ES: 'Cancelar' },
  history: { EN: 'History', ES: 'Historial' },
  hideHistory: { EN: 'Hide history', ES: 'Ocultar historial' },
  edit: { EN: 'Edit', ES: 'Editar' },
  remove: { EN: 'Remove', ES: 'Quitar' },
  overdue: { EN: 'Overdue', ES: 'Atrasada' },
  onTheBoat: { EN: 'On the boat', ES: 'En el barco' },
  noSpot: { EN: 'Stored (spot not recorded)', ES: 'Guardada (sin lugar)' },
  loadFailed: { EN: 'Couldn\'t load the vendor list.', ES: 'No se pudo cargar la lista.' },
  saveFailed: { EN: 'Couldn\'t save.', ES: 'No se pudo guardar.' },
  removeAsk: { EN: 'Remove "{name}"? Only do this if it was added by mistake.', ES: '¿Quitar "{name}"? Solo si se agregó por error.' },
  photoQueued: { EN: 'Saved. The photo will upload when there is signal.', ES: 'Guardado. La foto se subirá cuando haya señal.' },
  loadingHistory: { EN: 'Loading…', ES: 'Cargando…' },
  select: { EN: 'Select', ES: 'Elegir' },
  repairWaiting: { EN: 'Repair waiting', ES: 'Reparación pendiente' },
  details: { EN: 'Show buttons and history', ES: 'Ver botones e historial' },
  addRepair: { EN: '+ Repair', ES: '+ Reparación' },
  repairTitle: { EN: 'A repair the loft found', ES: 'Una reparación' },
  repairNote: { EN: 'What needs doing', ES: 'Qué hay que hacer' },
  repairNotePh: { EN: 'e.g. Replace zipper on the port panel', ES: '' },
  quote: { EN: 'Price to the customer (optional)', ES: 'Precio (opcional)' },
  addRepairBtn: { EN: 'Add repair', ES: 'Agregar' },
  recordAnswer: { EN: 'Record answer', ES: 'Anotar respuesta' },
  answerTitle: { EN: 'The customer\'s answer (by phone or in person)', ES: 'Respuesta del cliente' },
  answer: { EN: 'Answer', ES: 'Respuesta' },
  approvedOpt: { EN: 'Approved', ES: 'Aprobada' },
  declinedOpt: { EN: 'Declined', ES: 'Rechazada' },
  via: { EN: 'How', ES: 'Cómo' },
  phoneOpt: { EN: 'By phone', ES: 'Por teléfono' },
  personOpt: { EN: 'In person', ES: 'En persona' },
  answerNote: { EN: 'For a written answer, use Email repairs instead: the customer\'s reply is recorded with the time.', ES: '' },
  emailRepairs: { EN: 'Email repairs to approve', ES: 'Enviar reparaciones' },
  emailAgain: { EN: 'Email again (reminder)', ES: 'Enviar de nuevo' },
  priceToFollow: { EN: 'price to follow', ES: 'precio pendiente' },
  waitingPill: { EN: 'Needs approval', ES: 'Por aprobar' },
  sentPill: { EN: 'Sent to customer', ES: 'Enviada' },
  addCost: { EN: '+ Loft invoice', ES: '+ Factura' },
  costTitle: { EN: 'The loft\'s invoice: added to the work order', ES: 'Factura de la velería' },
  invoiceNo: { EN: 'Loft invoice #', ES: 'Factura #' },
  cost: { EN: 'Loft charged us', ES: 'Costo' },
  charge: { EN: 'Charge the customer (blank = same)', ES: 'Cobrar al cliente' },
  woRequired: { EN: 'Work order', ES: 'Orden de trabajo' },
  addCostBtn: { EN: 'Add to work order', ES: 'Agregar' },
  costAdded: { EN: 'Added to WO {wo}: {op}', ES: 'Agregado a la orden {wo}' },
  removeRepairAsk: { EN: 'Remove this repair note? It hasn\'t been sent to the customer.', ES: '¿Quitar esta reparación?' },
};
const TITLES = {
  pull: { EN: 'Pulled from the boat', ES: 'Quitada del barco' },
  sendOut: { EN: 'Send out to a loft', ES: 'Enviar a la velería' },
  return: { EN: 'Back from the loft', ES: 'De vuelta de la velería' },
  move: { EN: 'Move to another spot', ES: 'Mover a otro lugar' },
  install: { EN: 'Put back on the boat', ES: 'Poner en el barco' },
};

/** "Oct 12", or "Oct 12, 2025" when not this year. */
export function fmtDay(iso, lang = 'EN') {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const d = new Date(+m[1], +m[2] - 1, +m[3]);
  return d.toLocaleDateString(lang === 'ES' ? 'es-US' : 'en-US', { month: 'short', day: 'numeric', ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}) });
}
export const fmtAt = (ms, lang) => (ms ? fmtDay(formatDateToYYYYMMDD(new Date(ms)), lang) : '');

/** Where the piece is, in words. */
export function whereText(p, lang = 'EN', now = today()) {
  const es = lang === 'ES';
  if (p.Status === 'out') {
    const loft = p.Vendor_Name || (es ? 'la velería' : 'the loft');
    const since = p.Sent_On ? (es ? ` desde ${fmtDay(p.Sent_On, lang)}` : ` since ${fmtDay(p.Sent_On)}`) : '';
    const due = p.Due_Back ? (es ? `regresa ${fmtDay(p.Due_Back, lang)}` : `${isOverdue(p, now) ? 'was due' : 'due'} back ${fmtDay(p.Due_Back)}`) : '';
    return [`${es ? 'En' : 'At'} ${loft}${since}`, due].filter(Boolean).join(' · ');
  }
  if (p.Status === 'on-boat') return T.onTheBoat[lang];
  return p.Location || T.noSpot[lang];
}

/**
 * opts:
 *   root          the element the cards and forms are drawn in (or a function returning it)
 *   getPieces()   the pieces on screen; redraw() draws the owner's page again
 *   reload()      fetches fresh pieces after a save (live listeners may do it too)
 *   boat          { customerId, boatId } for the Add form (omit where there's no Add)
 *   lang          'EN' | 'ES' (or a function returning it)
 *   names(p)      { boat, customer } shown on each card (the board), or null
 *   actions       which moves to offer (default: all)
 *   photos        offer a photo on Add and Mark pulled
 *   savePhoto(id, file)  where a photo goes (default: upload now). May return 'queued'.
 *   addStatus     the Add form's starting "Where is it now" ('on-boat' | 'stored'); addLocked hides the choice
 *   edit, history, remove   offer those links (default true)
 *   selectable(p) show a checkbox on the card (the board's batch moves); selected: Set of ids
 *   onSelect()    a checkbox changed
 *   repairs       office: the piece's repairs (add, record an answer, email) and the loft's invoice
 *   onEmail(p)    email this boat's waiting repairs to the customer (canvasData.emailRepairs)
 *   rows          one slim line per piece; a click opens its buttons, repairs and history (the board)
 */
export function pieceController(opts) {
  const o = { actions: null, photos: false, edit: true, history: true, remove: true, names: null, addStatus: 'on-boat', addLocked: false, selected: new Set(), repairs: false, rows: false, ...opts };
  const lang = () => (typeof o.lang === 'function' ? o.lang() : o.lang) || 'EN';
  const rootEl = () => (typeof o.root === 'function' ? o.root() : o.root);
  const t = key => T[key][lang()];
  let form = null;        // { kind: 'add' | 'edit' | <action>, id? }
  let draft = {};         // what's typed: a redraw resets fields to their markup, so they're drawn from here
  let file = null;        // a picked photo (a redraw would clear the file input)
  let error = '';
  let saving = false;
  let spots = [];
  let lofts = null;
  const open = new Set(); // pieces whose history is showing
  let expanded = null;    // rows: the one piece opened
  const logs = new Map(); // pieceId → [entries], newest first (loaded when opened)
  const urls = new Map(); // photo path → download URL, written into the markup (a redraw drops attributes it doesn't have)
  const fetching = new Set();
  const listId = `cv-spots-${Math.random().toString(36).slice(2, 8)}`;

  const val = (name, fallback = '') => esc(draft[name] ?? fallback);
  const sel = (name, value, fallback = '') => ((draft[name] ?? fallback) === value ? ' selected' : '');
  const spotField = label => `
    <label class="cv-field"><span>${esc(label)}</span>
      <input type="text" name="location" list="${listId}" maxlength="120" value="${val('location')}" placeholder="${esc(t('spotPh'))}" autocomplete="off">
    </label>`;
  const noteField = () => `<label class="cv-field cv-field-wide"><span>${esc(t('note'))}</span><input type="text" name="note" maxlength="500" value="${val('note')}"></label>`;
  const woField = (fallback = '') => `<label class="cv-field cv-field-narrow"><span>${esc(t('wo'))}</span><input type="text" name="wo" maxlength="40" value="${val('wo', fallback)}"></label>`;
  const photoField = () => (o.photos ? `
    <label class="cv-field cv-photo-field"><span>${esc(t('photo'))}</span>
      <input type="file" name="photo" accept="image/*" capture="environment">
      ${file ? `<span class="cv-photo-picked">${esc(file.name || 'photo.jpg')}</span>` : ''}
    </label>` : '');
  const kindSelect = (fallback = '') => `
    <label class="cv-field"><span>${esc(t('whatIsIt'))}</span>
      <select name="kind">
        <option value="">${esc(t('choose'))}</option>
        ${GROUPS.filter(g => g.key !== 'all').map(g => `<optgroup label="${esc(groupLabel(g.key, lang()))}">${KINDS.filter(k => k.group === g.key).map(k => `<option value="${k.key}"${sel('kind', k.key, fallback)}>${esc(kindLabel(k.key, lang()))}</option>`).join('')}</optgroup>`).join('')}
      </select>
    </label>`;
  const buttons = label => `
    ${error ? `<div class="cv-error">${esc(error)}</div>` : ''}
    <div class="cv-form-buttons">
      <button type="button" class="cv-btn cv-btn-primary" data-cv-save${saving ? ' disabled' : ''}>${esc(saving ? t('saving') : label)}</button>
      <button type="button" class="cv-btn" data-cv-cancel>${esc(t('cancel'))}</button>
    </div>`;

  function formHTML(p) {
    if (form.kind === 'add') {
      const status = draft.status ?? o.addStatus;
      return `<div class="cv-form" data-key="form-add">
        <div class="cv-form-title">${esc(t(o.addLocked && o.addStatus === 'stored' ? 'addPulledTitle' : 'addTitle'))}</div>
        <div class="cv-form-grid">
          ${kindSelect()}
          <label class="cv-field"><span>${esc(t('description'))}</span><input type="text" name="label" maxlength="120" value="${val('label')}" placeholder="${esc(t('descriptionPh'))}"></label>
          ${o.addLocked ? '' : `<label class="cv-field"><span>${esc(t('whereNow'))}</span>
            <select name="status"><option value="on-boat">${esc(t('onBoatOpt'))}</option><option value="stored"${status === 'stored' ? ' selected' : ''}>${esc(t('pulledOpt'))}</option></select>
          </label>`}
          ${spotField(t(o.addLocked ? 'storedAt' : 'storedAtIfPulled'))}
          ${woField(o.boat?.wo || '')}
          ${photoField()}
        </div>
        ${buttons(t('add').replace('+ ', ''))}
      </div>`;
    }
    if (form.kind === 'repair') {
      return `<div class="cv-form">
        <div class="cv-form-title">${esc(t('repairTitle'))}</div>
        <div class="cv-form-grid">
          <label class="cv-field cv-field-wide"><span>${esc(t('repairNote'))}</span><input type="text" name="note" maxlength="500" value="${val('note')}" placeholder="${esc(t('repairNotePh'))}"></label>
          <label class="cv-field cv-field-narrow"><span>${esc(t('quote'))}</span><input type="text" name="quote" inputmode="decimal" maxlength="12" value="${val('quote')}" placeholder="$"></label>
        </div>
        ${buttons(t('addRepairBtn'))}
      </div>`;
    }
    if (form.kind === 'answer') {
      return `<div class="cv-form">
        <div class="cv-form-title">${esc(t('answerTitle'))}</div>
        <div class="cv-form-grid">
          <label class="cv-field"><span>${esc(t('answer'))}</span><select name="decision"><option value="">${esc(t('choose'))}</option><option value="approved"${sel('decision', 'approved')}>${esc(t('approvedOpt'))}</option><option value="declined"${sel('decision', 'declined')}>${esc(t('declinedOpt'))}</option></select></label>
          <label class="cv-field"><span>${esc(t('via'))}</span><select name="via"><option value="phone"${sel('via', 'phone')}>${esc(t('phoneOpt'))}</option><option value="in-person"${sel('via', 'in-person')}>${esc(t('personOpt'))}</option></select></label>
        </div>
        <div class="cv-hint">${esc(t('answerNote'))}</div>
        ${buttons(t('save'))}
      </div>`;
    }
    if (form.kind === 'cost') {
      return `<div class="cv-form">
        <div class="cv-form-title">${esc(t('costTitle'))}</div>
        <div class="cv-form-grid">
          <label class="cv-field"><span>${esc(t('loft'))}</span>
            <select name="vendorId">${lofts
              ? `<option value="">${esc(t('choose'))}</option>${lofts.map(v => `<option value="${esc(v.id)}"${sel('vendorId', v.id, p.Vendor_ID || '')}>${esc(v.name)}</option>`).join('')}`
              : `<option value="${esc(p.Vendor_ID || '')}">${esc(p.Vendor_Name || t('loadingLofts'))}</option>`}</select>
          </label>
          <label class="cv-field cv-field-narrow"><span>${esc(t('invoiceNo'))}</span><input type="text" name="invoiceNo" maxlength="40" value="${val('invoiceNo')}"></label>
          <label class="cv-field cv-field-narrow"><span>${esc(t('cost'))}</span><input type="text" name="cost" inputmode="decimal" maxlength="12" value="${val('cost')}" placeholder="$"></label>
          <label class="cv-field cv-field-narrow"><span>${esc(t('charge'))}</span><input type="text" name="charge" inputmode="decimal" maxlength="12" value="${val('charge')}" placeholder="$"></label>
          <label class="cv-field cv-field-narrow"><span>${esc(t('woRequired'))}</span><input type="text" name="wo" maxlength="40" value="${val('wo', p.WO || '')}"></label>
        </div>
        ${buttons(t('addCostBtn'))}
      </div>`;
    }
    if (form.kind === 'edit') {
      return `<div class="cv-form">
        <div class="cv-form-grid">
          ${kindSelect(p.Kind)}
          <label class="cv-field"><span>${esc(t('description'))}</span><input type="text" name="label" maxlength="120" value="${val('label', p.Label || '')}"></label>
        </div>
        ${buttons(t('save'))}
      </div>`;
    }
    const fields = {
      pull: () => spotField(t('storedAt')) + woField(p.WO || o.boat?.wo || '') + photoField(),
      sendOut: () => `
        <label class="cv-field"><span>${esc(t('loft'))}</span>
          <select name="vendorId">${lofts
            ? `<option value="">${esc(t('choose'))}</option>${lofts.map(v => `<option value="${esc(v.id)}"${sel('vendorId', v.id, p.Vendor_ID || '')}>${esc(v.name)}</option>`).join('')}`
            : `<option value="">${esc(t('loadingLofts'))}</option>`}</select>
        </label>
        <label class="cv-field cv-field-narrow"><span>${esc(t('dueBack'))}</span><input type="date" name="dueBack" value="${val('dueBack')}"></label>`,
      return: () => spotField(t('storedAt')),
      move: () => spotField(t('newSpot')),
      install: () => '',
    }[form.kind]();
    return `<div class="cv-form">
      <div class="cv-form-title">${esc(TITLES[form.kind][lang()])}</div>
      <div class="cv-form-grid">${fields}${noteField()}</div>
      ${buttons(actionLabel(form.kind, lang()))}
    </div>`;
  }

  function cardHTML(p) {
    if (o.rows) return rowHTML(p);
    const L = lang();
    const overdue = isOverdue(p, today());
    const n = o.names ? o.names(p) : null;
    return `
      <div class="cv-piece${overdue ? ' cv-overdue' : ''}${o.selected.has(p.id) ? ' cv-selected' : ''}" data-key="${esc(p.id)}">
        <div class="cv-piece-top">
          ${checkHTML(p)}
          <span class="cv-piece-name">${esc(pieceName(p, L))}</span>
          <span class="cv-status cv-st-${esc(p.Status || 'on-boat')}">${esc(statusLabel(p.Status, L))}</span>
          ${overdue ? `<span class="cv-status cv-st-overdue">${esc(t('overdue'))}</span>` : ''}
          ${p.WO ? `<span class="cv-wo">${esc(woLabel(p.WO))}</span>` : ''}
        </div>
        ${n ? `<div class="cv-piece-boat">${esc([n.boat, n.customer].filter(Boolean).join(' · '))}</div>` : ''}
        <div class="cv-piece-where">${esc(whereText(p, L))}</div>
        ${detailsHTML(p)}
      </div>`;
  }

  const checkHTML = p => (o.selectable && o.selectable(p)
    ? `<input type="checkbox" class="cv-check" data-cv-select="${esc(p.id)}" aria-label="${esc(t('select'))}"${o.selected.has(p.id) ? ' checked' : ''}>`
    : '');

  // One line: piece, boat and owner, where it is, flags. Opening it shows what a card shows below its top.
  function rowHTML(p) {
    const L = lang();
    const overdue = isOverdue(p, today());
    const n = o.names ? o.names(p) : null;
    const isOpen = expanded === p.id || (form && form.id === p.id);
    const waiting = Object.values(p.Repairs || {}).some(r => r && (r.Status === 'needs-approval' || r.Status === 'sent'));
    return `
      <div class="cv-row${isOpen ? ' cv-row-open' : ''}${overdue ? ' cv-overdue' : ''}${o.selected.has(p.id) ? ' cv-selected' : ''}" data-key="${esc(p.id)}">
        <div class="cv-row-line">
          <span class="cv-row-check">${checkHTML(p)}</span>
          <div class="cv-row-main" data-cv-toggle="${esc(p.id)}" role="button" tabindex="0" aria-expanded="${isOpen}" title="${esc(t('details'))}">
            <span class="cv-row-piece cv-piece-name">${esc(pieceName(p, L))}</span>
            <span class="cv-row-boat">${esc(n ? [n.boat, n.customer].filter(Boolean).join(' · ') : '')}</span>
            <span class="cv-row-where">${esc(whereText(p, L))}</span>
            <span class="cv-row-tags">
              ${overdue ? `<span class="cv-status cv-st-overdue">${esc(t('overdue'))}</span>` : ''}
              ${waiting ? `<span class="cv-status cv-st-out">${esc(t('repairWaiting'))}</span>` : ''}
              ${p.WO ? `<span class="cv-wo">${esc(woLabel(p.WO))}</span>` : ''}
            </span>
            <span class="cv-row-chev" aria-hidden="true">›</span>
          </div>
        </div>
        ${isOpen ? `<div class="cv-row-body">${detailsHTML(p)}</div>` : ''}
      </div>`;
  }

  function detailsHTML(p) {
    const L = lang();
    const offered = actionsFor(p).filter(a => !o.actions || o.actions.includes(a));
    const hist = logs.get(p.id);
    return `
        ${p.Photo ? `<a class="cv-photo" data-cv-photo="${esc(p.Photo)}" target="_blank" rel="noopener"${urls.get(p.Photo) ? ` href="${esc(urls.get(p.Photo))}" style="background-image: url(&quot;${esc(urls.get(p.Photo))}&quot;);"` : ''}></a>` : ''}
        <div class="cv-piece-actions">
          ${offered.map(a => `<button type="button" class="cv-btn${a === 'move' ? '' : ' cv-btn-strong'}" data-cv-act="${a}" data-id="${esc(p.id)}">${esc(actionLabel(a, L))}</button>`).join('')}
          ${o.history ? `<button type="button" class="cv-link" data-cv-hist="${esc(p.id)}">${esc(open.has(p.id) ? t('hideHistory') : t('history'))}</button>` : ''}
          ${o.edit ? `<button type="button" class="cv-link" data-cv-edit="${esc(p.id)}">${esc(t('edit'))}</button>` : ''}
          ${o.remove && !p.Moved_At ? `<button type="button" class="cv-link cv-link-danger" data-cv-remove="${esc(p.id)}">${esc(t('remove'))}</button>` : ''}
          ${o.repairs ? `<button type="button" class="cv-link" data-cv-repair="${esc(p.id)}">${esc(t('addRepair'))}</button>
          <button type="button" class="cv-link" data-cv-cost="${esc(p.id)}">${esc(t('addCost'))}</button>` : ''}
        </div>
        ${o.repairs ? repairsHTML(p) : ''}
        ${form && form.id === p.id ? formHTML(p) : ''}
        ${open.has(p.id) ? `<ol class="cv-history">${hist ? hist.map(e => `
          <li><span class="cv-h-date">${esc(fmtAt(e.At, L))}</span> ${esc(describeLog(e))}${e.By ? ` <span class="cv-h-by">· ${esc(e.By)}</span>` : ''}</li>`).join('') : `<li class="cv-h-by">${esc(t('loadingHistory'))}</li>`}</ol>` : ''}`;
  }

  // The piece's repairs (waiting ones first) and the loft's invoices, with what can be done to each.
  function repairsHTML(p) {
    const reps = Object.entries(p.Repairs || {}).filter(([, r]) => r)
      .sort(([, a], [, b]) => (WAITING_FIRST[a.Status] ?? 9) - (WAITING_FIRST[b.Status] ?? 9) || (a.Added_At || 0) - (b.Added_At || 0));
    const costs = Object.values(p.Costs || {}).filter(Boolean).sort((a, b) => (a.Added_At || 0) - (b.Added_At || 0));
    if (!reps.length && !costs.length) return '';
    const waiting = reps.filter(([, r]) => r.Status === 'needs-approval' || r.Status === 'sent').length;
    return `<div class="cv-repairs">
      ${reps.map(([rid, r]) => {
        const open = r.Status === 'needs-approval' || r.Status === 'sent';
        return `<div class="cv-repair cv-repair--${esc(r.Status)}" data-key="rep-${esc(rid)}">
          <span class="cv-repair-note">${esc(r.Note)}</span>
          <span class="cv-repair-amt">${r.Quote_Cents != null ? esc(money(r.Quote_Cents)) : `<span class="cv-h-by">${esc(t('priceToFollow'))}</span>`}</span>
          <span class="cv-status ${open ? (r.Status === 'sent' ? 'cv-st-stored' : 'cv-st-out') : r.Status === 'approved' ? 'cv-st-returned' : 'cv-st-overdue'}">${esc(open ? t(r.Status === 'sent' ? 'sentPill' : 'waitingPill') : decidedText(r))}</span>
          ${open ? `<button type="button" class="cv-link" data-cv-answer="${esc(p.id)}" data-rid="${esc(rid)}">${esc(t('recordAnswer'))}</button>` : ''}
          ${r.Status === 'needs-approval' ? `<button type="button" class="cv-link cv-link-danger" data-cv-rmrepair="${esc(p.id)}" data-rid="${esc(rid)}">${esc(t('remove'))}</button>` : ''}
        </div>`;
      }).join('')}
      ${waiting && o.onEmail ? `<button type="button" class="cv-btn cv-btn-strong cv-email" data-cv-email="${esc(p.id)}">${esc(t(reps.some(([, r]) => r.Status === 'needs-approval') ? 'emailRepairs' : 'emailAgain'))}</button>` : ''}
      ${costs.map(c => `<div class="cv-cost" data-key="cost-${esc(c.Part_ID || c.Added_At)}">${esc(describeLog({ Action: 'cost', Invoice_No: c.Invoice_No, Cost_Cents: c.Cost_Cents, WO: c.WO }))}${c.Charge_Cents !== c.Cost_Cents ? ` · charged ${esc(money(c.Charge_Cents))}` : ''}${c.Vendor_Name ? ` · ${esc(c.Vendor_Name)}` : ''}</div>`).join('')}
    </div>`;
  }

  const addFormHTML = () => (form && form.kind === 'add' ? formHTML() : '');
  const addButtonHTML = () => `<button type="button" class="cv-btn cv-add" data-cv-add>${esc(t('add'))}</button>`;
  const datalistHTML = () => `<datalist id="${listId}">${spots.map(s => `<option value="${esc(s)}"></option>`).join('')}</datalist>`;

  function start(kind, id = null, rid = null) {
    form = { kind, id, rid };
    if (id) expanded = id;
    draft = {};
    file = null;
    error = '';
    o.redraw();
    rootEl().querySelector('.cv-form input:not([type=file]), .cv-form select')?.focus();
    // Suggestions arrive after the form is up; the list is patched in, the form is left alone.
    if (['add', 'pull', 'return', 'move'].includes(kind)) loadSpots().then(s => { spots = s; o.redraw(); }).catch(() => {});
    if ((kind === 'sendOut' || kind === 'cost') && !lofts) {
      loadLofts().then(v => { lofts = v; o.redraw(); }).catch(err => { console.error('Canvas: vendors failed', err); error = t('loadFailed'); o.redraw(); });
    }
  }

  const close = () => { form = null; draft = {}; file = null; error = ''; };
  const values = () => Object.fromEntries([...rootEl().querySelectorAll('.cv-form [name]')].filter(el => el.type !== 'file').map(el => [el.name, el.value.trim()]));

  async function photoFor(id) {
    if (!file) return;
    const put = o.savePhoto || ((pid, f) => uploadPhoto(pid, f));
    if ((await put(id, file)) === 'queued') o.toast?.(t('photoQueued'));
  }

  async function save() {
    if (saving) return;
    const v = values();
    const piece = o.getPieces().find(p => p.id === form.id);
    saving = true; error = ''; o.redraw();
    try {
      if (form.kind === 'add') {
        const id = newPieceId();
        await addPiece({ id, customerId: o.boat.customerId, boatId: o.boat.boatId, kind: v.kind, label: v.label, status: o.addLocked ? o.addStatus : (v.status || o.addStatus), location: v.location, wo: v.wo, note: v.note });
        await photoFor(id);
      } else if (form.kind === 'repair') {
        await addRepair(piece, { note: v.note, quote: v.quote });
      } else if (form.kind === 'answer') {
        await recordRepairAnswer(piece, form.rid, v.decision, v.via || 'phone');
        logs.delete(piece.id);
      } else if (form.kind === 'cost') {
        const op = await addLoftInvoice(piece, { vendorId: v.vendorId, vendorName: (lofts || []).find(l => l.id === v.vendorId)?.name || piece.Vendor_Name || '', invoiceNo: v.invoiceNo, cost: v.cost, charge: v.charge, wo: v.wo });
        o.toast?.(t('costAdded').replace('{wo}', v.wo).replace('{op}', op.Short_Description || op.Description || ''));
        logs.delete(piece.id);
      } else if (form.kind === 'edit') {
        if (!v.kind) throw Object.assign(new Error('need-kind'), { canvasCode: 'need-kind' });
        const changes = {};
        if (v.kind !== piece.Kind) changes.Kind = v.kind;
        if (v.label !== (piece.Label || '')) changes.Label = v.label;
        if (Object.keys(changes).length) await editPiece(piece.id, changes);
      } else {
        const input = { ...v };
        if (form.kind === 'sendOut') input.vendorName = (lofts || []).find(l => l.id === v.vendorId)?.name || '';
        await runAction(piece, form.kind, input);
        logs.delete(piece.id);
        if (form.kind === 'pull') await photoFor(piece.id);
      }
      saving = false;
      close();
      await o.reload();
    } catch (err) {
      saving = false;
      error = errorText(err, lang()) || t('saveFailed');
      if (!err.canvasCode) console.error('Canvas: save failed', err);
    }
    o.redraw();
  }

  async function showHistory(id) {
    if (open.has(id)) { open.delete(id); o.redraw(); return; }
    open.add(id);
    o.redraw();
    try {
      const entries = Object.values(await loadLog(id)).filter(e => e && e.At).sort((a, b) => b.At - a.At);
      logs.set(id, entries);
    } catch (err) {
      console.error('Canvas: history failed', err);
      logs.set(id, []);
    }
    o.redraw();
  }

  function wire() {
    const root = rootEl();
    // Handlers are properties: the page is patched, so kept elements must not collect duplicates.
    const add = root.querySelector('[data-cv-add]');
    if (add) add.onclick = () => start('add');
    root.querySelectorAll('[data-cv-act]').forEach(b => { b.onclick = () => start(b.dataset.cvAct, b.dataset.id); });
    root.querySelectorAll('[data-cv-edit]').forEach(b => { b.onclick = () => start('edit', b.dataset.cvEdit); });
    root.querySelectorAll('[data-cv-repair]').forEach(b => { b.onclick = () => start('repair', b.dataset.cvRepair); });
    root.querySelectorAll('[data-cv-cost]').forEach(b => { b.onclick = () => start('cost', b.dataset.cvCost); });
    root.querySelectorAll('[data-cv-answer]').forEach(b => { b.onclick = () => start('answer', b.dataset.cvAnswer, b.dataset.rid); });
    root.querySelectorAll('[data-cv-rmrepair]').forEach(b => { b.onclick = async () => {
      const p = o.getPieces().find(x => x.id === b.dataset.cvRmrepair);
      if (!p || !confirm(t('removeRepairAsk'))) return;
      try { await removeRepair(p, b.dataset.rid); logs.delete(p.id); await o.reload(); } catch (err) { o.toast?.(err.message); }
      o.redraw();
    }; });
    root.querySelectorAll('[data-cv-email]').forEach(b => { b.onclick = async () => {
      const p = o.getPieces().find(x => x.id === b.dataset.cvEmail);
      if (!p) return;
      try { await o.onEmail(p); } catch (err) { o.toast?.(err.message || 'Could not open the email.'); }
    }; });
    root.querySelectorAll('[data-cv-toggle]').forEach(r => {
      const toggle = () => {
        const id = r.dataset.cvToggle;
        expanded = expanded === id || (form && form.id === id) ? null : id;
        if (form && form.id !== expanded) close(); // the form's row is closing, or another row opened
        o.redraw();
      };
      r.onclick = toggle;
      r.onkeydown = e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } };
    });
    root.querySelectorAll('[data-cv-hist]').forEach(b => { b.onclick = () => showHistory(b.dataset.cvHist); });
    root.querySelectorAll('[data-cv-remove]').forEach(b => { b.onclick = async () => {
      const p = o.getPieces().find(x => x.id === b.dataset.cvRemove);
      if (!p || !confirm(t('removeAsk').replace('{name}', pieceName(p, lang())))) return;
      try { await removePiece(p.id); await o.reload(); } catch (err) { console.error('Canvas: remove failed', err); }
      o.redraw();
    }; });
    root.querySelectorAll('[data-cv-select]').forEach(c => { c.onchange = () => {
      if (c.checked) o.selected.add(c.dataset.cvSelect); else o.selected.delete(c.dataset.cvSelect);
      o.onSelect?.();
    }; });
    const saveBtn = root.querySelector('[data-cv-save]');
    if (saveBtn) saveBtn.onclick = save;
    const cancel = root.querySelector('[data-cv-cancel]');
    if (cancel) cancel.onclick = () => { close(); o.redraw(); };
    root.querySelectorAll('.cv-form [name]').forEach(el => {
      if (el.type === 'file') { el.onchange = () => { file = el.files[0] || null; o.redraw(); }; return; }
      el.oninput = el.onchange = () => { draft[el.name] = el.value; };
      if (el.tagName === 'INPUT') el.onkeydown = e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel?.onclick(); };
    });
    root.querySelectorAll('[data-cv-photo]').forEach(a => {
      const path = a.dataset.cvPhoto;
      if (urls.has(path) || fetching.has(path)) return;
      fetching.add(path);
      photoUrl(path).then(url => { fetching.delete(path); urls.set(path, url); if (url) o.redraw(); });
    });
  }

  return {
    cardHTML, addFormHTML, addButtonHTML, datalistHTML, wire, start,
    /** A form is open: owners hold live redraws until it closes. */
    busy: () => !!form,
    /** Close any open form (e.g. the piece it was for was moved by someone else). */
    close,
  };
}
